// Read-only hooks for the #750 rank views — student progress + owner
// promotion history + gym-wide rank distribution.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';

// ============================================================================
// Student-facing: a single student's rank progress in every discipline
// ============================================================================

export interface StudentProgressRow {
  student_rank_id: string;
  sport_id: string;
  sport_name?: string;
  state: 'active' | 'on_hold';
  current_rank_id: string;
  current_rank_name?: string;
  current_rank_order: number;
  achieved_at: string;
  accumulated_time_on_hold_seconds: number;
  hold_started_at: string | null;
  profile_name?: string;
  total_ranks_in_profile: number;
  next_rank_id?: string;
  next_rank_name?: string;
  next_time_threshold_days: number | null;
  next_attendance_threshold: number | null;
  days_at_rank: number;
  attendance_since: number;
}

export function useStudentProgress(studentId: string | undefined) {
  return useQuery({
    queryKey: ['student-progress', studentId ?? 'none'],
    queryFn: async (): Promise<StudentProgressRow[]> => {
      if (!studentId) return [];

      // 1. All ranks this student holds (across companies).
      const { data: ranks, error } = await supabase
        .from('student_ranks')
        .select('*')
        .eq('student_id', studentId)
        .order('state', { ascending: true });
      if (error) throw error;
      const rows = ranks ?? [];
      if (rows.length === 0) return [];

      // 2. Hydrate rank + profile + sport names.
      const rankIds = rows.map((r) => r.current_rank_id);
      const sportIds = Array.from(new Set(rows.map((r) => r.sport_id)));
      const [currentRanksRes, sportsRes] = await Promise.all([
        supabase
          .from('belt_ranks')
          .select('id, name, rank_order, profile_id, time_required_days, attendance_required')
          .in('id', rankIds),
        supabase.from('company_sports').select('id, sport_name').in('id', sportIds),
      ]);
      if (currentRanksRes.error) throw currentRanksRes.error;
      if (sportsRes.error) throw sportsRes.error;
      const currentRankById = new Map(
        (currentRanksRes.data ?? []).map((r) => [r.id, r])
      );

      const profileIds = Array.from(
        new Set((currentRanksRes.data ?? []).map((r) => r.profile_id))
      );

      const [profilesRes, allProfileRanksRes] = await Promise.all([
        supabase.from('belt_profiles').select('id, name').in('id', profileIds),
        supabase
          .from('belt_ranks')
          .select('id, name, profile_id, rank_order, time_required_days, attendance_required')
          .in('profile_id', profileIds)
          .order('rank_order', { ascending: true }),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (allProfileRanksRes.error) throw allProfileRanksRes.error;

      const profileById = new Map((profilesRes.data ?? []).map((p) => [p.id, p]));
      const sportById = new Map((sportsRes.data ?? []).map((s) => [s.id, s]));

      // Group ranks by profile_id for next-rank lookups + counts.
      const ranksByProfile = new Map<string, typeof allProfileRanksRes.data>();
      for (const r of allProfileRanksRes.data ?? []) {
        const arr = ranksByProfile.get(r.profile_id) ?? [];
        arr.push(r);
        ranksByProfile.set(r.profile_id, arr);
      }

      // 3. Attendance since each achieved_at, per (sport, company).
      const enrollmentCounts = await Promise.all(
        rows.map(async (r) => {
          const { count } = await supabase
            .from('class_enrollments')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', studentId)
            .eq('attended', true)
            .not('checked_in_at', 'is', null)
            .gt('checked_in_at', r.achieved_at);
          // Note: ideally also filter by company/sport via a join; for read-side
          // approximation this is close enough. Scan job uses the strict filter.
          return count ?? 0;
        })
      );

      return rows.map((r, i): StudentProgressRow => {
        const current = currentRankById.get(r.current_rank_id);
        const profile = current ? profileById.get(current.profile_id) : undefined;
        const profileRanks = current ? (ranksByProfile.get(current.profile_id) ?? []) : [];
        const next = profileRanks.find(
          (br) => current && br.rank_order === current.rank_order + 1
        );
        const daysAtRank =
          Math.floor((Date.now() - new Date(r.achieved_at).getTime()) / 86400000) -
          Math.floor(r.accumulated_time_on_hold_seconds / 86400);
        return {
          student_rank_id: r.id,
          sport_id: r.sport_id,
          sport_name: sportById.get(r.sport_id)?.sport_name,
          state: r.state as 'active' | 'on_hold',
          current_rank_id: r.current_rank_id,
          current_rank_name: current?.name,
          current_rank_order: current?.rank_order ?? 0,
          achieved_at: r.achieved_at,
          accumulated_time_on_hold_seconds: r.accumulated_time_on_hold_seconds,
          hold_started_at: r.hold_started_at,
          profile_name: profile?.name,
          total_ranks_in_profile: profileRanks.length,
          next_rank_id: next?.id,
          next_rank_name: next?.name,
          next_time_threshold_days: next?.time_required_days ?? null,
          next_attendance_threshold: next?.attendance_required ?? null,
          days_at_rank: Math.max(daysAtRank, 0),
          attendance_since: enrollmentCounts[i],
        };
      });
    },
    enabled: !!studentId,
    staleTime: 60_000,
  });
}

// ============================================================================
// Owner-facing: company-wide promotion history with filters
// ============================================================================

export interface PromotionHistoryRow {
  id: string;
  promoted_at: string;
  is_transfer_setup: boolean;
  notes: string | null;
  promoted_by: string;
  promoter_name?: string;
  student_id: string;
  student_name?: string;
  sport_id: string;
  sport_name?: string;
  from_rank_name?: string;
  to_rank_name: string;
  profile_name?: string;
}

export interface HistoryFilters {
  sportId?: string;
  startDate?: string;
  endDate?: string;
}

export function useCompanyPromotionHistory(filters: HistoryFilters) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['belt-history', companyId, filters],
    queryFn: async (): Promise<PromotionHistoryRow[]> => {
      if (!companyId) return [];

      // Get the student_ranks scoped to this company so we can filter
      // belt_promotions through them.
      let srQuery = supabase
        .from('student_ranks')
        .select('id, student_id, sport_id')
        .eq('company_id', companyId);
      if (filters.sportId) srQuery = srQuery.eq('sport_id', filters.sportId);

      const { data: srs, error: srErr } = await srQuery;
      if (srErr) throw srErr;
      const srRows = srs ?? [];
      if (srRows.length === 0) return [];

      const srIds = srRows.map((r) => r.id);

      let promotionsQuery = supabase
        .from('belt_promotions')
        .select('*')
        .in('student_rank_id', srIds)
        .order('promoted_at', { ascending: false });
      if (filters.startDate) promotionsQuery = promotionsQuery.gte('promoted_at', filters.startDate);
      if (filters.endDate) promotionsQuery = promotionsQuery.lte('promoted_at', filters.endDate);

      const { data: promotions, error: pErr } = await promotionsQuery;
      if (pErr) throw pErr;
      const proms = promotions ?? [];
      if (proms.length === 0) return [];

      // Hydrate display names.
      const rankIds = Array.from(
        new Set([
          ...proms.map((p) => p.to_rank_id),
          ...proms.map((p) => p.from_rank_id).filter((x): x is string => !!x),
        ])
      );
      const studentIds = Array.from(new Set(srRows.map((r) => r.student_id)));
      const sportIds = Array.from(new Set(srRows.map((r) => r.sport_id)));
      const promoterIds = Array.from(new Set(proms.map((p) => p.promoted_by)));

      const [ranksRes, profilesPivotRes, studentsRes, sportsRes, promotersRes] = await Promise.all([
        supabase.from('belt_ranks').select('id, name, profile_id').in('id', rankIds),
        Promise.resolve({ data: null, error: null }), // filled below
        supabase.from('user_profiles').select('id, first_name, last_name').in('id', [...studentIds, ...promoterIds]),
        supabase.from('company_sports').select('id, sport_name').in('id', sportIds),
        Promise.resolve({ data: null, error: null }),
      ]);
      if (ranksRes.error) throw ranksRes.error;
      if (studentsRes.error) throw studentsRes.error;
      if (sportsRes.error) throw sportsRes.error;

      const profileIds = Array.from(
        new Set((ranksRes.data ?? []).map((r) => r.profile_id))
      );
      const profilesRes = await supabase
        .from('belt_profiles')
        .select('id, name')
        .in('id', profileIds);
      if (profilesRes.error) throw profilesRes.error;

      const srById = new Map(srRows.map((s) => [s.id, s]));
      const rankById = new Map((ranksRes.data ?? []).map((r) => [r.id, r]));
      const profileById = new Map(
        (profilesRes.data ?? []).map((p) => [p.id, p])
      );
      const userById = new Map((studentsRes.data ?? []).map((u) => [u.id, u]));
      const sportById = new Map((sportsRes.data ?? []).map((s) => [s.id, s]));

      const nameOf = (id: string) => {
        const u = userById.get(id);
        if (!u) return undefined;
        return `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || undefined;
      };

      return proms.map((p): PromotionHistoryRow => {
        const sr = srById.get(p.student_rank_id)!;
        const toRank = rankById.get(p.to_rank_id);
        const fromRank = p.from_rank_id ? rankById.get(p.from_rank_id) : undefined;
        const profile = toRank ? profileById.get(toRank.profile_id) : undefined;
        return {
          id: p.id,
          promoted_at: p.promoted_at,
          is_transfer_setup: p.is_transfer_setup,
          notes: p.notes,
          promoted_by: p.promoted_by,
          promoter_name: nameOf(p.promoted_by),
          student_id: sr.student_id,
          student_name: nameOf(sr.student_id),
          sport_id: sr.sport_id,
          sport_name: sportById.get(sr.sport_id)?.sport_name,
          from_rank_name: fromRank?.name,
          to_rank_name: toRank?.name ?? '(unknown)',
          profile_name: profile?.name,
        };
      });
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

// ============================================================================
// Owner-facing: gym-wide rank distribution for a discipline (or all)
// ============================================================================

export interface RankDistributionRow {
  rank_id: string;
  rank_name: string;
  rank_order: number;
  profile_name?: string;
  sport_id: string;
  sport_name?: string;
  active_count: number;
  on_hold_count: number;
}

export function useRankDistribution(sportId?: string) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['belt-distribution', companyId, sportId ?? 'all'],
    queryFn: async (): Promise<RankDistributionRow[]> => {
      if (!companyId) return [];

      let q = supabase
        .from('student_ranks')
        .select('current_rank_id, sport_id, state')
        .eq('company_id', companyId);
      if (sportId) q = q.eq('sport_id', sportId);

      const { data: rows, error } = await q;
      if (error) throw error;
      const list = rows ?? [];
      if (list.length === 0) return [];

      // Aggregate client-side keyed on current_rank_id.
      const counts = new Map<string, { active: number; on_hold: number; sport_id: string }>();
      for (const r of list) {
        const k = r.current_rank_id;
        const v = counts.get(k) ?? { active: 0, on_hold: 0, sport_id: r.sport_id };
        if (r.state === 'active') v.active += 1;
        else v.on_hold += 1;
        counts.set(k, v);
      }

      const rankIds = Array.from(counts.keys());
      const sportIds = Array.from(new Set(list.map((r) => r.sport_id)));
      const [ranksRes, sportsRes] = await Promise.all([
        supabase
          .from('belt_ranks')
          .select('id, name, rank_order, profile_id')
          .in('id', rankIds),
        supabase.from('company_sports').select('id, sport_name').in('id', sportIds),
      ]);
      if (ranksRes.error) throw ranksRes.error;
      if (sportsRes.error) throw sportsRes.error;

      const profileIds = Array.from(
        new Set((ranksRes.data ?? []).map((r) => r.profile_id))
      );
      const profilesRes = await supabase
        .from('belt_profiles')
        .select('id, name')
        .in('id', profileIds);
      if (profilesRes.error) throw profilesRes.error;

      const profileById = new Map(
        (profilesRes.data ?? []).map((p) => [p.id, p])
      );
      const sportById = new Map(
        (sportsRes.data ?? []).map((s) => [s.id, s])
      );

      return (ranksRes.data ?? [])
        .map((r): RankDistributionRow => {
          const c = counts.get(r.id)!;
          return {
            rank_id: r.id,
            rank_name: r.name,
            rank_order: r.rank_order,
            profile_name: profileById.get(r.profile_id)?.name,
            sport_id: c.sport_id,
            sport_name: sportById.get(c.sport_id)?.sport_name,
            active_count: c.active,
            on_hold_count: c.on_hold,
          };
        })
        .sort(
          (a, b) =>
            (a.sport_name ?? '').localeCompare(b.sport_name ?? '') ||
            a.rank_order - b.rank_order
        );
    },
    enabled: !!companyId,
    staleTime: 60_000,
  });
}
